import React from 'react';
import PolarCheckout from '../components/PolarCheckout';
import PricingCard from '../components/ui/PricingCard';
import FAQItem from '../components/ui/FAQItem';

const PricingPage = () => {
  const monthlyFeatures = [
    'Automated daily commits',
    '1-5 commits per day',
    'Random commit mode',
    'Calendar dashboard',
    'Email support'
  ];

  const yearlyFeatures = [
    'Everything in Monthly',
    'Priority support',
    'Advanced analytics',
    'Early feature access'
  ];

  const faqItems = [
    {
      question: 'Is StreakKeeper safe to use with my GitHub account?',
      answer: 'Absolutely. StreakKeeper uses GitHub personal access tokens with limited permissions. We only create commits to dedicated repositories and never access your existing code or private repositories.'
    },
    {
      question: 'How do the commits look in my GitHub history?',
      answer: 'The commits appear completely natural with meaningful commit messages like "docs: update project documentation" or "feat: add utility functions". They\'re designed to blend seamlessly with your normal development activity.'
    },
    {
      question: 'What\'s the maximum commit limit per day?',
      answer: 'We limit commits to a maximum of 5 per day to ensure your GitHub activity looks natural and professional. This prevents any appearance of spam or artificial inflation of your contribution graph.'
    },
    {
      question: 'Can I customize the commit frequency?',
      answer: 'Yes! You can set anywhere from 1-5 commits per day, or enable random mode to vary the frequency naturally. This helps match your typical development patterns.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-full text-sm font-medium text-blue-700 mb-6">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Simple & Transparent
          </div>
          <h1 className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-6">
            Choose Your 
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Perfect Plan
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Start maintaining your GitHub streak today with our professional-grade automation service. 
            Cancel anytime, no questions asked.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          <PricingCard
            title="Monthly"
            price="19"
            period="month"
            description="Perfect for trying out StreakKeeper"
            features={monthlyFeatures}
          >
            <PolarCheckout planType="monthly">
              <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-8 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                Get Started - $19/month
              </button>
            </PolarCheckout>
          </PricingCard>

          <PricingCard
            title="Yearly"
            price="199"
            period="year"
            description="Save $29 per year with our annual plan"
            features={yearlyFeatures}
            featured={true}
            badge="BEST VALUE"
          >
            <PolarCheckout planType="yearly">
              <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-8 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                Get Started - $199/year
              </button>
            </PolarCheckout>
          </PricingCard>
        </div>

        {/* Trust Signals */}
        <div className="text-center mb-20">

        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about StreakKeeper
            </p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Start Your Streak?
            </h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of developers who trust StreakKeeper to maintain their GitHub presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PolarCheckout planType="monthly">
                <button className="inline-flex items-center px-8 py-4 bg-white text-blue-600 hover:bg-gray-50 font-semibold rounded-xl transition-all duration-300 hover:shadow-lg">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Start Monthly - $19
                </button>
              </PolarCheckout>
              <PolarCheckout planType="yearly">
                <button className="inline-flex items-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  Save with Yearly - $199
                </button>
              </PolarCheckout>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;